import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '@Components';
import {
  useCreateSelection,
  useGetFundData,
  useGetFundMatches,
  useGetFundSelectionsPaginated,
  useGetFundSelections,
  useGetFundSelectionsData,
  useRejectMatch,
  useDeleteSelection,
  useFundInvitesProject,
} from '@Hooks/Api';
import { useGetFundProjects } from '@Hooks/Api/useGetFundProjects';
import { useUpdateFundVotingConfiguration } from '@Hooks/Api/useUpdateFundVotingConfiguration';
import { useRouter } from '@Helpers/useRouter';
import { PathParams } from '@App/paths';
import { FundVotingConfiguration } from '@Types';
import { SelectionStatusEnum } from '@Types/Selection';
import { useNotificationsContext } from '@Contexts';

import { FundManagement, VotingModalStatus } from './FundManagement';
import router from 'next/router';
import { useGetCurrentTab } from '@Hooks/useGetCurrentTab';
import { useGetFundGivingEngine } from '@Hooks/Api/useGetFundGivingEngine';

const SHORTLIST_STATUSES = [
  SelectionStatusEnum.SHORTLISTED,
  SelectionStatusEnum.UNAVAILABLE,
  SelectionStatusEnum.NO_LONGER_MATCHES,
];
const INVITED_STATUSES = [SelectionStatusEnum.INVITED, SelectionStatusEnum.INVITATION_REJECTED];

export const FundManagementController = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [getFundData, { loading: isFundDataLoading, data: fundData }] = useGetFundData();
  const [getDueDiligenceProjects, { data: dueDiligenceProjects, loading: isDueDiligenceLoading }] =
    useGetFundProjects();
  const [
    getAwaitingPaymentProjects,
    { data: awaitingPaymentProjects, loading: isAwaitingPaymentLoading },
  ] = useGetFundProjects();
  const [getFundedProjects, { data: fundedProjects, loading: isFundedProjectsLoading }] =
    useGetFundProjects();
  const [getCompletedProjects, { data: completedProjects, loading: isCompletedProjectsLoading }] =
    useGetFundProjects();
  const [getFundGivingEngine, { data: fundGivingEngine, loading: isFundGivingEngineLoading }] =
    useGetFundGivingEngine();

  const totalDueDiligenceCount = dueDiligenceProjects?.results.length;
  const totalAwaitingPaymentCount = awaitingPaymentProjects?.results.length;
  const totalFundedProjectsCount = fundedProjects?.results.length;
  const totalCompletedProjectsCount = completedProjects?.results.length;
  const [getFundMatches, { paginatedData: matchesPaginatedResponse }] = useGetFundMatches();
  const {
    page: matchList,
    loading: isFundMatchesLoading,
    totalCount: totalMatchesCount,
  } = matchesPaginatedResponse;

  const [getShortlistSelections, { paginatedData: shortlistPaginatedResponse }] =
    useGetFundSelectionsPaginated();
  const {
    page: shortlistedSelections,
    loading: isShortlistSelectionsLoading,
    totalCount: totalShortlistCount,
  } = shortlistPaginatedResponse;
  // Used to get the data for selections with status SHORTLISTED
  const [
    getShortlistedSelectionData,
    { data: shortlistedSelectionData, loading: isShortlistedSelectionDataLoading },
  ] = useGetFundSelectionsData();
  // Used to get the data for all selections in the shortlist (multiple statuses)
  const [
    getShortlistSelectionsData,
    { data: shortlistSelectionsData, loading: isShortlistSelectionsDataLoading },
  ] = useGetFundSelectionsData();
  const [getInvitedSelections, { loading: isInvitedSelectionsLoading, data: invitedSelections }] =
    useGetFundSelections();
  const [createSelection] = useCreateSelection();
  const [rejectMatch] = useRejectMatch();
  const [deleteSelection] = useDeleteSelection();
  const [fundInvitesProject] = useFundInvitesProject();
  const [
    updateFundVotingConfiguration,
    { error: votingConfigurationUpdateErrors, loading: votingConfigurationUpdateLoading },
  ] = useUpdateFundVotingConfiguration();
  const { params } = useRouter();
  const fundId = Number(params[PathParams.FUND_ID]);
  const { success: successNotification, error: errorNotification } = useNotificationsContext();
  const [votingModalStatus, setVotingModalStatus] = useState<VotingModalStatus>(
    VotingModalStatus.CLOSED,
  );
  const { t } = useTranslation('fund');

  useEffect(() => {
    if (fundId) {
      setIsInitialized(true);
      getFundData(fundId);
      getFundGivingEngine(fundId);
    }
  }, [getFundData, fundId, getFundGivingEngine]);

  useEffect(() => {
    if (fundId) {
      getFundMatches(fundId);
    }
  }, [fundId, getFundMatches]);

  useEffect(() => {
    if (fundId) {
      getShortlistSelections(fundId, SHORTLIST_STATUSES);
      getShortlistedSelectionData(fundId, [SelectionStatusEnum.SHORTLISTED]);
      getInvitedSelections(fundId, INVITED_STATUSES);
      getShortlistSelectionsData(fundId, SHORTLIST_STATUSES);
    }
  }, [
    fundId,
    getShortlistSelections,
    getShortlistedSelectionData,
    getInvitedSelections,
    getShortlistSelectionsData,
  ]);

  useEffect(() => {
    getDueDiligenceProjects(fundId, { detailedProjectStatus: 'DUE_DILIGENCE' });
  }, [fundId, getDueDiligenceProjects]);

  useEffect(() => {
    getAwaitingPaymentProjects(fundId, { detailedProjectStatus: 'AWAITING_PAYMENT' });
  }, [fundId, getAwaitingPaymentProjects]);

  useEffect(() => {
    getFundedProjects(fundId, { detailedProjectStatus: 'FUNDED' });
  }, [fundId, getFundedProjects]);

  useEffect(() => {
    getCompletedProjects(fundId, { detailedProjectStatus: ['COMPLETE', 'SURVEY_DONE'] });
  }, [fundId, getCompletedProjects]);

  const moveProjectToShortlist = useCallback(
    (projectId: number, projectName: string) =>
      createSelection(fundId, projectId)
        .then(() => {
          successNotification(t('management.add_to_shortlist_success', { projectName }));
          getFundMatches(fundId);
          getShortlistSelections(fundId, SHORTLIST_STATUSES);
          getShortlistedSelectionData(fundId, [SelectionStatusEnum.SHORTLISTED]);
          getShortlistSelectionsData(fundId, SHORTLIST_STATUSES);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] || t('management.add_to_shortlist_fail', { projectName }),
          );
        }),
    [
      createSelection,
      errorNotification,
      fundId,
      getFundMatches,
      getShortlistSelections,
      getShortlistedSelectionData,
      getShortlistSelectionsData,
      successNotification,
      t,
    ],
  );

  const rejectMatchedProject = useCallback(
    (matchId: number, projectName: string) =>
      rejectMatch(matchId)
        .then(() => {
          successNotification(t('management.reject_match_success', { projectName }));
          getFundMatches(fundId);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] || t('management.reject_match_fail', { projectName }),
          );
        }),
    [errorNotification, fundId, getFundMatches, rejectMatch, successNotification, t],
  );

  const removeProjectFromShortlist = useCallback(
    (selectionId: number, projectName: string) =>
      deleteSelection(fundId, selectionId)
        .then(() => {
          successNotification(
            t('management.remove_project_from_shortlist_success', { projectName }),
          );
          getFundMatches(fundId);
          getShortlistSelections(fundId, SHORTLIST_STATUSES);
          getShortlistedSelectionData(fundId, [SelectionStatusEnum.SHORTLISTED]);
          getShortlistSelectionsData(fundId, SHORTLIST_STATUSES);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] ||
              t('management.remove_project_from_shortlist_fail', { projectName }),
          );
        }),
    [
      deleteSelection,
      errorNotification,
      fundId,
      getFundMatches,
      getShortlistSelections,
      getShortlistedSelectionData,
      getShortlistSelectionsData,
      successNotification,
      t,
    ],
  );

  const inviteProjectToFund = useCallback(
    (selectionId: number, projectName: string) =>
      fundInvitesProject(fundId, selectionId)
        .then(() => {
          successNotification(t('management.invite_project_to_fund_success', { projectName }));
          getFundData(fundId);
          getShortlistSelections(fundId, SHORTLIST_STATUSES);
          getShortlistedSelectionData(fundId, [SelectionStatusEnum.SHORTLISTED]);
          getInvitedSelections(fundId, INVITED_STATUSES);
          getShortlistSelectionsData(fundId, SHORTLIST_STATUSES);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] || t('management.invite_project_to_fund_fail', { projectName }),
          );
        }),
    [
      errorNotification,
      fundId,
      fundInvitesProject,
      getFundData,
      getShortlistSelections,
      getShortlistedSelectionData,
      getInvitedSelections,
      getShortlistSelectionsData,
      successNotification,
      t,
    ],
  );

  const getSelectionIdForProject = (projectId: number): number | undefined => {
    // Check both shortlist and invited selections to find the project
    const shortlistSelection = shortlistedSelections?.find(
      (selection) => selection.project.id === projectId,
    );
    if (shortlistSelection) return shortlistSelection.id;

    const invitedSelection = invitedSelections?.find(
      (selection) => selection.project.id === projectId,
    );
    return invitedSelection?.id;
  };

  const totalShortlistedSelectionsCount = useMemo(() => {
    if (!shortlistedSelectionData?.total_count) return 0;
    return shortlistedSelectionData.total_count;
  }, [shortlistedSelectionData]);

  const tabs = useMemo(
    () => [
      {
        title: t('management.tabs.matches'),
        slug: 'matches',
        amount: isFundMatchesLoading ? undefined : totalMatchesCount,
      },
      {
        title: t('management.tabs.shortlist'),
        slug: 'shortlist',
        amount: isShortlistedSelectionDataLoading ? undefined : totalShortlistedSelectionsCount,
      },
      {
        title: t('management.tabs.invitations'),
        slug: 'invitations',
        amount: isInvitedSelectionsLoading ? undefined : invitedSelections?.length,
      },
      {
        title: t('management.tabs.due_diligence'),
        slug: 'due-diligence',
        amount: isDueDiligenceLoading ? undefined : totalDueDiligenceCount,
      },
      {
        title: t('management.tabs.awaiting_payment'),
        slug: 'awaiting-payment',
        amount: isAwaitingPaymentLoading ? undefined : totalAwaitingPaymentCount,
      },
      {
        title: t('management.tabs.funded_projects'),
        slug: 'funded-projects',
        amount: isFundedProjectsLoading ? undefined : totalFundedProjectsCount,
      },
      {
        title: t('management.tabs.complete'),
        slug: 'complete',
        amount: isCompletedProjectsLoading ? undefined : totalCompletedProjectsCount,
      },
    ],
    [
      t,
      isFundMatchesLoading,
      totalMatchesCount,
      isShortlistedSelectionDataLoading,
      totalShortlistedSelectionsCount,
      isInvitedSelectionsLoading,
      invitedSelections?.length,
      isDueDiligenceLoading,
      isAwaitingPaymentLoading,
      isFundedProjectsLoading,
      totalFundedProjectsCount,
      isCompletedProjectsLoading,
      totalDueDiligenceCount,
      totalAwaitingPaymentCount,
      totalCompletedProjectsCount,
    ],
  );

  // Set tab based on router and update modal state
  let currentTab = useGetCurrentTab(params.fundManageSlug?.[0], tabs);

  const setTab = useCallback(
    (tabNumber: number) => {
      return router.push(`/fund/${fundId}/manage/${tabs[tabNumber].slug}`, undefined, {
        shallow: true,
      });
    },
    [fundId, tabs],
  );

  const onVotingConfigurationSubmit = useCallback(
    (data: FundVotingConfiguration) => {
      const isCreating = fundData?.voting_configuration == null;
      const fixedData = {
        ...data,
        // react-hook-form doesn't let us use null as a value, so replace ""
        // with null before sending to the server.
        maximum_votes_per_user: data.maximum_votes_per_user || null,
      };
      updateFundVotingConfiguration(fundId, fixedData, fundData?.voting_configuration?.id).then(
        () => {
          // Refetch fund data (including voting configuration) to update the page.
          getFundData(fundId);
          if (isCreating) {
            // After creation, we show the sharing link modal.
            setVotingModalStatus(VotingModalStatus.SHARING);
          } else {
            setVotingModalStatus(VotingModalStatus.CLOSED);
          }
        },
      );
    },
    [fundId, fundData, getFundData, updateFundVotingConfiguration],
  );

  if (isFundDataLoading || !isInitialized) return <Loader />;

  return (
    <>
      {fundData && (
        <FundManagement
          fundId={fundId}
          fundData={fundData}
          tabs={tabs}
          currentTab={currentTab}
          setCurrentTab={setTab}
          isMatchesListLoading={
            isFundMatchesLoading || isShortlistSelectionsLoading || isInvitedSelectionsLoading
          }
          isSelectionsListLoading={isShortlistSelectionsLoading || isInvitedSelectionsLoading}
          shortlistPaginatedResponse={shortlistPaginatedResponse}
          shortlistSelectionsData={shortlistSelectionsData}
          isShortlistSelectionsDataLoading={isShortlistSelectionsDataLoading}
          invitedSelections={invitedSelections || []}
          dueDiligenceProjectsList={dueDiligenceProjects?.results || []}
          isDueDiligenceLoading={isDueDiligenceLoading}
          awaitingPaymentProjectsList={awaitingPaymentProjects?.results || []}
          isAwaitingPaymentLoading={isAwaitingPaymentLoading}
          fundedProjectsList={fundedProjects?.results || []}
          isFundedProjectsLoading={isFundedProjectsLoading}
          completedProjectsList={completedProjects?.results || []}
          isCompletedProjectsLoading={isCompletedProjectsLoading}
          moveProjectToShortlist={moveProjectToShortlist}
          rejectMatchedProject={rejectMatchedProject}
          removeProjectFromShortlist={removeProjectFromShortlist}
          inviteProjectToFund={inviteProjectToFund}
          onVotingConfigurationSubmit={onVotingConfigurationSubmit}
          votingModalStatus={votingModalStatus}
          setVotingModalStatus={setVotingModalStatus}
          votingConfigurationUpdateErrors={votingConfigurationUpdateErrors}
          votingConfigurationUpdateLoading={votingConfigurationUpdateLoading}
          matchesPaginatedResponse={matchesPaginatedResponse}
          fundGivingEngine={fundGivingEngine}
          isFundGivingEngineLoading={isFundGivingEngineLoading}
          getSelectionIdForProject={getSelectionIdForProject}
          refreshAwaitingPaymentProjects={getAwaitingPaymentProjects}
          refreshFundedProjects={getFundedProjects}
        />
      )}
    </>
  );
};
